"use client";

import {
  createContext,
  useContext,
  useCallback,
  type ReactNode,
} from "react";
import type { Locale } from "./types";
import { DEFAULT_LOCALE } from "./types";
import type { TranslationKeys } from "./locales/vi";
import vi from "./locales/vi";
import en from "./locales/en";
import { useRouter, usePathname } from "next/navigation";

const translations: Record<Locale, TranslationKeys> = { vi, en };

interface I18nContextValue {
  locale: Locale;
  t: TranslationKeys;
  switchLocale: (newLocale: Locale) => void;
  localePath: (path: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * URL-based I18nProvider.
 * Receives `locale` prop from the [locale] layout (server component reads URL).
 */
export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  const switchLocale = useCallback((newLocale: Locale) => {
    // Replace /vi/... or /en/... with /newLocale/...
    const segments = pathname.split("/");
    segments[1] = newLocale;
    router.push(segments.join("/"));
  }, [pathname, router]);

  const localePath = useCallback((path: string) => {
    return `/${locale}${path.startsWith("/") ? path : `/${path}`}`;
  }, [locale]);

  const t = translations[locale];

  return (
    <I18nContext.Provider value={{ locale, t, switchLocale, localePath }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    return {
      locale: DEFAULT_LOCALE as Locale,
      t: translations[DEFAULT_LOCALE],
      switchLocale: () => {},
      localePath: (path: string) => `/${DEFAULT_LOCALE}${path.startsWith("/") ? path : `/${path}`}`,
    };
  }
  return context;
}
